import React from 'react';
import AuthorizedPage from '../../component/AuthorizedPage';
import ProductApi from '../../api/ProductApi';
import ProductItemApi from '../../api/ProductItemApi';
import { Button, Dropdown, Form, Modal, Table } from 'react-bootstrap';
import { PlusSquare } from 'react-bootstrap-icons';
import If from '../../component/If';
import { updateState, loadingStart, loadingComplete, upsertStateIn} from "../StateHelper";

const SearchableDropdown = ({
  items,
  selectedValue,
  onSelect,
  placeholder,
}: {
  items: any[];
  selectedValue: number | null;
  onSelect: (id: number | null) => void;
  placeholder: string;
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const selectedItem = items.find((i) => i.id === selectedValue);
  const filtered = items.filter((i) =>
    i.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  return (
    <Dropdown>
      <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start d-flex justify-content-between align-items-center bg-white">
        <span className="text-truncate">{selectedItem ? selectedItem.name : placeholder}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="w-100 p-2 shadow" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <Form.Control
          autoFocus
          placeholder="Ara..."
          className="mb-2 sticky-top"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {filtered.length > 0 ? (
          filtered.map((i) => (
            <Dropdown.Item key={i.id} onClick={() => onSelect(i.id)} active={selectedValue === i.id}>
              {i.name}
            </Dropdown.Item>
          ))
        ) : (
          <div className="text-muted text-center small p-2">Sonuç bulunamadı</div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

interface BundleItem {
  productItemId: number;
  quantity: number;
  packagingTypeDescription?: { name: string; label: string };
  packagingUnitTypeDescription?: { name: string; label: string };
}

interface ProductForm {
  id?: number;
  name: string;
  stockCode: string;
  barcode: string;
  price: number;
  discount: number;
  onlineListingAvailable: number;
  truckShippingAvailable: number;
  quantityInsideBox: number;
  notes: string;
  productBundle: BundleItem[];
}

interface State {
  productsRaw: any[];
  products: any[];
  productPoolItems: any[];
  showModal: boolean;
  showAddBundleItemModal: boolean;
  showBundleViewModal: boolean;
  productForm: ProductForm;
  newBundleItem: {
    productItemId: number | null;
    quantity: number | null;
  };
  viewingBundleItems: BundleItem[];
  viewingProductName: string;
  page: number;
  totalPages: number;
  search: string;
  pageSize: number;
  showPassive: boolean;
}

const initialState: State = {
  productsRaw: [],
  products: [],
  productPoolItems: [],
  showModal: false,
  showAddBundleItemModal: false,
  showBundleViewModal: false,
  productForm: {
    name: '',
    stockCode: '',
    barcode: '',
    price: 0,
    discount: 0,
    onlineListingAvailable: 1,
    truckShippingAvailable: 1,
    quantityInsideBox: 0,
    notes: '',
    productBundle: [],
  },
  newBundleItem: {
    productItemId: null,
    quantity: null,
  },
  viewingBundleItems: [],
  viewingProductName: '',
  page: 0,
  totalPages: 0,
  search: '',
  pageSize: 50,
  showPassive: false,
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

class ListingProductWizard extends React.Component<any, State> {
  searchTimer: any;

  constructor(props: any) {
    super(props);
    this.state = { ...initialState };

    this.fetchProducts = this.fetchProducts.bind(this);
    this.loadProductItems = this.loadProductItems.bind(this);
    this.openModalForProduct = this.openModalForProduct.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleNewBundleItemChange = this.handleNewBundleItemChange.bind(this);
    this.addNewBundleItem = this.addNewBundleItem.bind(this);
    this.removeBundleItem = this.removeBundleItem.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.openBundleViewModal = this.openBundleViewModal.bind(this);
    this.closeBundleViewModal = this.closeBundleViewModal.bind(this);
    this.onSearch = this.onSearch.bind(this);
  }

  componentDidMount() {
    this.fetchProducts();
    this.loadProductItems();
  }

  onSearch(filteredProducts: any) {
    this.setState({ products: filteredProducts });
  }

  // Server-side: arama + sayfa backend'e gider; aktif/pasif moduna gore endpoint secilir
  loadProducts = async (page: number = this.state.page, search: string = this.state.search, size: number = this.state.pageSize) => {
    const res = this.state.showPassive
      ? await ProductApi.listPagedPassive(search, page, size)
      : await ProductApi.listPagedSearch(search, page, size);
    if (res.success && res.data) {
      this.setState({
        products: res.data.content || [],
        totalPages: res.data.totalPages || 0,
        page: res.data.number || 0,
      });
    }
  };

  // Mevcut yeniden-yukleme cagrilarini (submit/pasife al) korumak icin
  fetchProducts = () => {
    this.loadProducts();
  };

  onSearchChange = (value: string) => {
    this.setState({ search: value });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadProducts(0, value), 350);
  };

  onPageSizeChange = (size: number) => {
    this.setState({ pageSize: size });
    this.loadProducts(0, this.state.search, size);
  };

  togglePassive = () => {
    this.setState({ showPassive: !this.state.showPassive, page: 0 }, () => {
      this.loadProducts(0, this.state.search, this.state.pageSize);
    });
  };

  reactivateProduct = async (productId: number) => {
    if (!window.confirm('Bu ürünü tekrar aktif etmek istediğinize emin misiniz?')) return;
    try {
      const res = await ProductApi.reactivate(productId);
      if (res?.success) this.loadProducts();
      else alert('Aktif edilemedi.');
    } catch (error) {
      alert('Sunucu hatası: ' + error);
    }
  };

  renderPagination = () => {
    const { page, totalPages, pageSize } = this.state;
    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button size="sm" variant="outline-secondary" className="me-3" disabled={page <= 0} onClick={() => this.loadProducts(page - 1)}>← Önceki</Button>
        <span>Sayfa {totalPages === 0 ? 0 : page + 1} / {totalPages}</span>
        <Button size="sm" variant="outline-secondary" className="ms-3" disabled={page >= totalPages - 1} onClick={() => this.loadProducts(page + 1)}>Sonraki →</Button>
        <Form.Select
          size="sm"
          className="ms-4"
          style={{ width: 'auto' }}
          value={pageSize}
          onChange={(e) => this.onPageSizeChange(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} / sayfa</option>
          ))}
        </Form.Select>
      </div>
    );
  };

  loadProductItems = async () => {
    const res = await ProductItemApi.listAll();
    if (res.success) {
      this.setState({ productPoolItems: res.data });
    }
  };

  openModalForProduct = (product?: any) => {
    if (product) {
      this.setState({
        productForm: {
          ...product,
          productBundle: product.productBundle || [],
        },
        showModal: true,
      });
    } else {
      this.setState({
        productForm: {
          name: '',
          stockCode: '',
          barcode: '',
          price: 0,
          discount: 0,
          onlineListingAvailable: 1,
          truckShippingAvailable: 1,
          quantityInsideBox: 0,
          notes: '',
          productBundle: [],
        },
        showModal: true,
      });
    }
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      productForm: {
        ...prevState.productForm,
        [name]:
          name === 'price' ||
          name === 'discount' ||
          name === 'onlineListingAvailable' ||
          name === 'truckShippingAvailable' ||
          name === 'quantityInsideBox'
            ? Number(value)
            : value,
      },
    }));
  };

  handleNewBundleItemChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      newBundleItem: {
        ...prevState.newBundleItem,
        [name]: value === '' ? null : Number(value),
      },
    }));
  };

  addNewBundleItem = () => {
    const { newBundleItem, productForm } = this.state;

    if (
      newBundleItem.productItemId === null ||
      newBundleItem.quantity === null ||
      newBundleItem.quantity <= 0
    ) {
      alert('Lütfen geçerli bir ürün ve miktar seçiniz.');
      return;
    }

    const exists = productForm.productBundle.some(
      (item) => item.productItemId === newBundleItem.productItemId
    );
    if (exists) {
      alert('Bu ürün zaten içindekiler listesinde mevcut.');
      return;
    }

    const updatedBundle = [
      ...productForm.productBundle,
      {
        productItemId: newBundleItem.productItemId,
        quantity: newBundleItem.quantity,
      },
    ];

    this.setState({
      productForm: {
        ...productForm,
        productBundle: updatedBundle,
      },
      showAddBundleItemModal: false,
      newBundleItem: { productItemId: null, quantity: null },
    });
  };

  removeBundleItem = (productItemId: number) => {
    const { productForm } = this.state;
    const updatedBundle = productForm.productBundle.filter(
      (item) => item.productItemId !== productItemId
    );
    this.setState({
      productForm: {
        ...productForm,
        productBundle: updatedBundle,
      },
    });
  };

  // CREATE veya UPDATE ayrımı burada yapılıyor
  submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const { productForm } = this.state;

    const productItemIds = productForm.productBundle.map(item => item.productItemId);
    const payload = { ...productForm, productItemIds };

    try {
      let response;
      if (productForm.id) {
        response = await ProductApi.update(payload);
      } else {
        response = await ProductApi.create(payload);
      }

      if (response.success) {
        this.closeModal();
        this.fetchProducts();
      } else {
        alert('Hata: ' + (response.error?.message || ''));
      }
    } catch (error) {
      alert('Sunucu hatası: ' + error);
    }
  };

  // Soft delete — backend'de rowStatus = 0 yapıyor
  deactivateProduct = async (productId: number) => {
    if (!window.confirm('Bu ürünü pasif yapmak istediğinizden emin misiniz?')) return;
    try {
      await ProductApi.deactivate(productId);
      this.fetchProducts();
    } catch (error) {
      alert('Sunucu hatası: ' + error);
    }
  };

  openBundleViewModal = (product: any) => {
    this.setState({
      viewingBundleItems: product.productBundle || [],
      viewingProductName: product.name || '',
      showBundleViewModal: true,
    });
  };

  closeBundleViewModal = () => {
    this.setState({ showBundleViewModal: false, viewingBundleItems: [], viewingProductName: '' });
  };

  renderProductBundleTable = (bundleItems: BundleItem[]) => {
    if (!bundleItems || bundleItems.length === 0) {
      return <div>İçindekiler bilgisi yok.</div>;
    }
    return (
      <Table striped bordered hover size="sm" className="mt-3">
        <thead>
          <tr>
            <th>Ad</th>
            <th>Miktar</th>
            <th>Paketleme Tipi</th>
            <th>Paketleme Birimi</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {bundleItems.map((item) => {
            const productItem = this.state.productPoolItems.find(
              (pi) => pi.id === item.productItemId
            );
            return (
              <tr key={item.productItemId}>
                <td>{productItem ? productItem.name : 'Bilinmiyor'}</td>
                <td>{item.quantity}</td>
                <td>{item.packagingTypeDescription?.label || '—'}</td>
                <td>{item.packagingUnitTypeDescription?.label || '—'}</td>
                <td>
                  {this.state.showModal ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => this.removeBundleItem(item.productItemId)}
                    >
                      Sil
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  renderAddBundleItemModal = () => {
    const { showAddBundleItemModal, productPoolItems, newBundleItem } = this.state;
    return (
      <Modal
        show={showAddBundleItemModal}
        onHide={() => this.setState({ showAddBundleItemModal: false })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Yeni İçindekiler Ürünü Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Ürün Seç</Form.Label>
            <SearchableDropdown
              items={productPoolItems}
              selectedValue={newBundleItem.productItemId}
              onSelect={(id) => this.setState((prev) => ({ newBundleItem: { ...prev.newBundleItem, productItemId: id } }))}
              placeholder="Seçiniz"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Miktar</Form.Label>
            <Form.Control
              type="number"
              name="quantity"
              value={newBundleItem.quantity ?? ''}
              onChange={this.handleNewBundleItemChange}
              min={1}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showAddBundleItemModal: false })}>
            İptal
          </Button>
          <Button variant="primary" onClick={this.addNewBundleItem}>
            Ekle
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  render() {
    const { products, showModal, productForm, showBundleViewModal, viewingBundleItems, viewingProductName } = this.state;

    return (
      <AuthorizedPage>
        <div className="container mt-4">
          <h3>Satılabilir Ürün Listesi{this.state.showPassive ? ' — Pasifler' : ''}</h3>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="search-container">
              <input
                type="text"
                placeholder="Ara"
                value={this.state.search}
                onChange={(e) => this.onSearchChange(e.target.value)}
                className="search-input"
              />
              <div className="search-icon"></div>
            </div>
            <div>
              <Button
                variant={this.state.showPassive ? 'secondary' : 'outline-secondary'}
                className="me-2"
                onClick={this.togglePassive}
              >
                {this.state.showPassive ? '← Aktif Ürünlere Dön' : 'Pasif Ürünleri Listele'}
              </Button>
              <Button variant="success" onClick={() => this.openModalForProduct()}>
                <PlusSquare /> Yeni Ürün Ekle
              </Button>
            </div>
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Ad</th>
                <th>Stok Kodu</th>
                <th>Barkod</th>
                <th>Online Satış</th>
                <th>Tır Sevkiyat</th>
                <th>Kutu İçi Adet</th>
                <th>Açıklama</th>
                <th>İçindekiler</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>{product.name || '—'}</td>
                  <td>{product.stockCode || '—'}</td>
                  <td>{product.barcode || '—'}</td>
                  <td>{product.onlineListingAvailable ? 'Evet' : 'Hayır'}</td>
                  <td>{product.truckShippingAvailable ? 'Evet' : 'Hayır'}</td>
                  <td>{product.quantityInsideBox ?? '—'}</td>
                  <td>{product.notes || '—'}</td>
                  <td>
                    {product.productBundle && product.productBundle.length > 0 ? (
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => this.openBundleViewModal(product)}
                      >
                        Göster
                      </Button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.openModalForProduct(product)}
                    >
                      Düzenle
                    </Button>
                    {this.state.showPassive ? (
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => this.reactivateProduct(product.id)}
                      >
                        Geri Al
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => this.deactivateProduct(product.id)}
                      >
                        Pasife Al
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {this.renderPagination()}

          {/* Ürün Ekle / Düzenle Modalı */}
          <Modal size="lg" show={showModal} onHide={this.closeModal} scrollable>
            <Modal.Header closeButton>
              <Modal.Title>{productForm.id ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={this.submitForm}>
                <Form.Control
                  type="hidden"
                  name="id"
                  value={productForm.id || ''}
                  readOnly
                />

                <div className="row">
                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label><strong>Ad</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={productForm.name}
                      onChange={this.handleInputChange}
                      placeholder="Ürün adı giriniz"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label><strong>Stok Kodu</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="stockCode"
                      value={productForm.stockCode}
                      onChange={this.handleInputChange}
                      placeholder="Opsiyonel"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label><strong>Barkod</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="barcode"
                      value={productForm.barcode}
                      onChange={this.handleInputChange}
                      placeholder="Opsiyonel"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label><strong>Kutu İçi Adet</strong></Form.Label>
                    <Form.Control
                      type="number"
                      name="quantityInsideBox"
                      value={productForm.quantityInsideBox}
                      onChange={this.handleInputChange}
                      min={0}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6 d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      label="Online Satışta Göster"
                      name="onlineListingAvailable"
                      checked={!!productForm.onlineListingAvailable}
                      onChange={(e) =>
                        this.handleInputChange({
                          target: {
                            name: 'onlineListingAvailable',
                            value: e.target.checked ? 1 : 0,
                          },
                        } as any)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6 d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      label="Tır Sevkiyatına Uygun"
                      name="truckShippingAvailable"
                      checked={!!productForm.truckShippingAvailable}
                      onChange={(e) =>
                        this.handleInputChange({
                          target: {
                            name: 'truckShippingAvailable',
                            value: e.target.checked ? 1 : 0,
                          },
                        } as any)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label><strong>Açıklama</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={productForm.notes}
                      onChange={this.handleInputChange}
                      placeholder="Ürün hakkında açıklama"
                    />
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>İçindekiler</h5>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => this.setState({ showAddBundleItemModal: true })}
                  >
                    + Yeni İçindekiler Ürünü Ekle
                  </Button>
                </div>

                <div className="border rounded p-3 mb-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {this.renderProductBundleTable(productForm.productBundle)}
                </div>

                <div className="text-end">
                  <Button type="submit" variant="success" className="px-4 py-2 fw-bold">
                    Kaydet
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          {/* İçindekiler Ürünü Ekle Modalı */}
          {this.renderAddBundleItemModal()}

          {/* Sadece İçindekiler Görüntüleme Modalı */}
          <Modal show={showBundleViewModal} onHide={this.closeBundleViewModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>{viewingProductName} - İçindekiler</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {this.renderProductBundleTable(viewingBundleItems)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.closeBundleViewModal}>
                Kapat
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </AuthorizedPage>
    );
  }
}

export default ListingProductWizard;